import { useState } from 'react'
import { ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import { Alert, Button, Descriptions, Drawer, Tabs } from 'antd'
import { formatShanghaiDateTime } from '@/const/date.ts'
import { DRAWER_SIZE } from '@/const/drawer.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { LicenseDocumentPreview } from '@/features/license/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { EnterpriseSummary } from './enterprise-summary.tsx'

export function EnterpriseDetailDrawer({
  subjectId,
  onClose,
  onEdit,
  onSupplement
}: {
  subjectId: number
  onClose: () => void
  onEdit?: () => void
  onSupplement?: () => void
}) {
  const [open, setOpen] = useState(true)
  const trpc = useTRPC()
  const { currentSession } = useRouteContext({ from: '/_authenticated' })
  const query = useQuery(trpc.order.subjects.detail.queryOptions({ subjectId }))
  const subject = query.data
  const ids = [
    ...(currentSession.isSuperAdmin || currentSession.permissions.includes('company.read')
      ? [subject?.company?.businessLicenseFileId]
      : []),
    ...(currentSession.isSuperAdmin || currentSession.permissions.includes('identity.read')
      ? [subject?.identity?.frontFileId, subject?.identity?.backFileId]
      : [])
  ].filter((id): id is number => Boolean(id))
  const files = useQuery({ ...trpc.file.getUrls.queryOptions({ ids }), enabled: ids.length > 0 })
  const fileUrl = (id?: number | null) => files.data?.find((file) => file.id === id)?.url
  return (
    <Drawer
      open={open}
      title={`服务对象 #${subjectId}`}
      size={DRAWER_SIZE}
      loading={query.isLoading}
      onClose={() => setOpen(false)}
      afterOpenChange={(visible) => {
        if (!visible) {
          onClose()
        }
      }}
      extra={
        <div className="flex gap-2">
          {onEdit && (
            <Authorized permissions={['subject.update']}>
              <Button onClick={onEdit}>编辑基本信息</Button>
            </Authorized>
          )}
          {onSupplement && (
            <Authorized permissions={['subject.material.update']}>
              <Button type="primary" onClick={onSupplement}>
                补录资料
              </Button>
            </Authorized>
          )}
        </div>
      }
    >
      {query.error && (
        <Alert
          type="error"
          title={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      {subject && (
        <div className="space-y-5">
          {subject.isArchived && <Alert type="warning" title="企业已归档，不可用于新单；已有业务记录仍保留。" />}
          {subject.kind === 'person' && <Alert type="warning" title="历史个人对象，需人工核对，不会自动改为企业。" />}
          <Descriptions
            title="基本信息"
            size="small"
            column={2}
            items={[
              { key: 'customer', label: '所属客户', children: subject.customerName },
              { key: 'creator', label: '创建人', children: subject.creator.name },
              { key: 'created', label: '创建时间', children: formatShanghaiDateTime(subject.createdAt) },
              { key: 'updated', label: '更新时间', children: formatShanghaiDateTime(subject.updatedAt) },
              { key: 'note', label: '备注', children: subject.note || '—', span: 2 }
            ]}
          />
          <EnterpriseSummary enterprise={subject} />
          {files.error && (
            <Alert
              type="error"
              title="证件原件读取失败"
              action={<Button onClick={() => void files.refetch()}>重试</Button>}
            />
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <LicenseDocumentPreview
              src={fileUrl(subject.company?.businessLicenseFileId)}
              title="营业执照原件"
              loading={files.isLoading}
            />
            <LicenseDocumentPreview
              src={fileUrl(subject.identity?.frontFileId)}
              title="身份证人像面"
              loading={files.isLoading}
            />
          </div>
          {subject.identity?.backFileId && (
            <LicenseDocumentPreview
              src={fileUrl(subject.identity.backFileId)}
              title="身份证国徽面"
              loading={files.isLoading}
            />
          )}
          <Tabs
            items={[
              {
                key: 'rentals',
                label: '租赁记录',
                children: <EnterpriseBusinessRecords subjectId={subjectId} kind="rental" />
              },
              {
                key: 'services',
                label: '服务记录',
                children: <EnterpriseBusinessRecords subjectId={subjectId} kind="service" />
              }
            ]}
          />
        </div>
      )}
    </Drawer>
  )
}

function EnterpriseBusinessRecords({ subjectId, kind }: { subjectId: number; kind: 'rental' | 'service' }) {
  const trpc = useTRPC()
  const [page, setPage] = useState({ current: 1, pageSize: 10 })
  const rental = useQuery({
    ...trpc.order.rental.list.queryOptions({ ...page, subjectId, archiveStatus: 'all' }),
    enabled: kind === 'rental',
    placeholderData: keepPreviousData
  })
  const service = useQuery({
    ...trpc.order.service.list.queryOptions({ ...page, subjectId, archiveStatus: 'all' }),
    enabled: kind === 'service',
    placeholderData: keepPreviousData
  })
  const query = kind === 'rental' ? rental : service
  type Row = { id: number; orderNo: string; status: string; createdAt: Date }
  const columns: ProColumns<Row>[] = [
    { title: 'ID', dataIndex: 'id', copyable: true, width: '5rem' },
    { title: '订单号', dataIndex: 'orderNo', copyable: true },
    {
      title: '状态',
      dataIndex: 'status',
      valueEnum: {
        awaitingBilling: '待出账',
        pending: kind === 'rental' ? '待处理' : '待办理',
        active: '履约中',
        completed: '已完成',
        cancelled: '已取消',
        archived: '已归档'
      }
    },
    { title: '创建时间', dataIndex: 'createdAt', valueType: 'dateTime' }
  ]
  return (
    <>
      {query.error && <Alert type="error" title={query.error.message} />}
      <ProTable<Row>
        rowKey="id"
        search={false}
        columns={columns}
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        columnsState={{ persistenceKey: `service-enterprise-${kind}-records:table`, persistenceType: 'localStorage' }}
        toolBarRender={false}
        options={false}
        scroll={{ x: '35rem' }}
        pagination={{
          ...page,
          total: query.data?.total ?? 0,
          onChange: (current, pageSize) => setPage({ current, pageSize })
        }}
      />
    </>
  )
}
